import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/** @extends {Component<{pieces: HTMLUListElement, variants: HTMLScriptElement, note: HTMLElement}>} */
class CompleteLook extends Component {
  requiredRefs = ['pieces', 'variants'];
  #controller;
  #variants = [];
  #mainVariant;
  #sequence = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#load();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#sequence++;
  }

  async #load() {
    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;
    this.#sequence++;
    this.refs.pieces.replaceChildren();
    this.refs.note.hidden = true;
    try { this.#variants = JSON.parse(this.refs.variants.textContent); } catch { return; }
    this.#mainVariant = this.#variants.find((variant) => String(variant.id) === this.dataset.variantId);
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal: controller.signal });
    const url = new URL(this.dataset.searchUrl, location.origin);
    if (url.origin !== location.origin) return;
    const escapeQuery = (value) => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const words = this.dataset.collection.split(/\s+/).filter(Boolean);
    const vendor = this.dataset.vendorQuery || `vendor:"${escapeQuery(this.dataset.vendor)}"`;
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', 'bp-look-results');
    const queries = [];
    if (this.dataset.lookKey && words.length && words.length <= 8) queries.push({ kind: 'collection', q: `(${vendor}) AND (${words.map((word) => `title:"${escapeQuery(word)}"`).join(' AND ')})` });
    const colors = { verde: ['verde', 'green'], azul: ['azul', 'marino', 'navy', 'blue'], rosa: ['rosa', 'pink'], beige: ['beige', 'crudo', 'crema', 'lino', 'turrón'], granate: ['granate', 'burdeos', 'burgundy'], marron: ['marrón', 'brown'], gris: ['gris', 'grey', 'gray'], negro: ['negro', 'negra', 'black'], blanco: ['blanco', 'blanca', 'white'], camel: ['camel'], malva: ['malva', 'lila', 'lilac'] };
    const colorWords = colors[this.dataset.color];
    const footwearTerms = this.dataset.audience === 'boy' ? ['mocasines', 'náuticos'] : this.dataset.audience === 'baby' ? ['patucos', 'peuques'] : ['merceditas'];
    const complementaryTerms = [...footwearTerms, 'calcetines', 'calcetas', ...(this.dataset.audience === 'girl' ? ['leotardos'] : [])];
    if (this.dataset.audience !== 'any' && colorWords) queries.push({ kind: 'color', q: `(${colorWords.map((word) => `title:"${word}"`).join(' OR ')}) AND (${complementaryTerms.map((word) => `title:"${word}"`).join(' OR ')})` });
    if (!queries.length) return;
    try {
      const responses = await Promise.allSettled(queries.map(async (query) => {
        const requestUrl = new URL(url);
        requestUrl.searchParams.set('q', query.q);
        const response = await fetch(requestUrl, { signal: controller.signal });
        if (!response.ok) return [];
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const results = html.querySelector('[data-bp-look-results]');
        if (!results || (query.kind === 'collection' && results.querySelector('[data-look-results-incomplete]'))) return [];
        return [...results.querySelectorAll('[data-look-key]')].sort((a, b) => b.querySelectorAll('option[value]:not([value=""])').length - a.querySelectorAll('option[value]:not([value=""])').length).map((item) => ({ item, kind: query.kind }));
      }));
      if (!this.isConnected || controller.signal.aborted) return;
      const seen = new Set([this.dataset.productId]);
      const seenColorRoles = new Set();
      const items = responses.flatMap((response) => response.status === 'fulfilled' ? response.value : []).filter(({ item, kind }) => {
        if (seen.has(item.dataset.productId)) return false;
        const role = item.dataset.role;
        if (role === this.dataset.mainRole) return false;
        if (kind === 'collection') {
          if (item.dataset.lookKey !== this.dataset.lookKey || !['layer', 'hair', 'accessory', 'footwear', 'legwear'].includes(role)) return false;
        } else if (item.dataset.color !== this.dataset.color || !['footwear', 'legwear'].includes(role) || seenColorRoles.has(role)) return false;
        if (this.dataset.audience === 'boy' && (role === 'hair' || item.dataset.audience === 'girl' || item.dataset.audience === 'baby')) return false;
        if (this.dataset.audience === 'baby' && !['baby', 'any'].includes(item.dataset.audience)) return false;
        if (this.dataset.audience === 'girl' && ['boy', 'baby'].includes(item.dataset.audience)) return false;
        const included = { layer: /-(jersey|chaqueta|torera)(-|$)/, hair: /-(lazo|tocado|collar)(-|$)/, accessory: /-(patucos|capota|gorro)(-|$)/, footwear: /-merceditas(-|$)/, legwear: /-(calcetas|calcetines|leotardos)(-|$)/ }[role];
        if (included.test(`-${this.dataset.mainTitle}`)) return false;
        for (const link of item.querySelectorAll('a')) {
          const target = new URL(link.getAttribute('href'), location.origin);
          if (target.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(target.pathname)) return false;
        }
        seen.add(item.dataset.productId);
        if (kind === 'color') seenColorRoles.add(role);
        item.querySelector('[data-reason]').textContent = kind === 'collection' ? this.dataset.reasonCollection : this.dataset.reasonColor;
        return true;
      });
      const visibleItems = items.slice(0, 2).map(({ item }) => item);
      if (!visibleItems.length) return;
      for (const item of visibleItems) {
        const select = item.querySelector('select');
        const label = item.querySelector('label');
        select.id = `BP-Look-${this.dataset.instanceId}-${item.dataset.productId}`;
        label.htmlFor = select.id;
        select.dataset.initialPrice = item.querySelector('[data-piece-price]').textContent;
      }
      this.refs.pieces.append(...visibleItems);
      this.refs.note.hidden = false;
    } catch (error) {
      if (error.name !== 'AbortError') this.refs.note.hidden = true;
    }
  }

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card') || event.target.dataset.productId !== this.dataset.productId) return;
    const sequence = ++this.#sequence;
    this.#mainVariant = null;
    this.#updateTotals();
    event.promise?.then(({ detail }) => {
      if (!this.isConnected || sequence !== this.#sequence) return;
      const resource = detail?.resource;
      const variant = this.#variants.find((item) => String(item.id) === String(resource?.id));
      this.#mainVariant = variant && resource?.available ? { ...variant, price: resource.price } : null;
      this.#updateTotals();
    }).catch(() => {});
  };

  /** @param {Event} event - Native choice of an independently stocked complementary variant. */
  choosePiece(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;
    this.#updatePiece(event.target);
  }

  #updatePiece(select) {
    const piece = select.closest('[data-look-key]');
    if (!piece || !this.contains(piece)) return;
    const option = select.selectedOptions[0];
    piece.querySelector('[data-piece-price]').textContent = select.value ? option.dataset.money : select.dataset.initialPrice;
    const link = piece.querySelector('[data-piece-visit]');
    const url = new URL(link.href, location.origin);
    if (select.value) url.searchParams.set('variant', select.value);
    else url.searchParams.delete('variant');
    link.href = url.pathname + url.search;
    this.#updateTotals();
  }

  #updateTotals() {
    for (const piece of this.refs.pieces.querySelectorAll('[data-look-key]')) {
      const select = piece.querySelector('select');
      const total = piece.querySelector('[data-piece-total]');
      const price = Number(select.selectedOptions[0]?.dataset.price);
      const mainPrice = Number(this.#mainVariant?.price);
      const show = Boolean(select.value && this.#mainVariant?.available && Number.isFinite(price) && Number.isFinite(mainPrice));
      total.hidden = !show;
      if (show) piece.querySelector('[data-total-value]').textContent = new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency: this.dataset.currency }).format((mainPrice + price) / 100);
    }
  }
}

if (!customElements.get('bp-complete-look')) customElements.define('bp-complete-look', CompleteLook);
